"use client";
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { PRODUCTS, CATEGORIES, Product, getStockLabel, formatPrice } from '@/lib/store';

export default function ProductShopPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [cartToast, setCartToast] = useState<string | null>(null);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [shuffledFeatured, setShuffledFeatured] = useState<typeof PRODUCTS>([]);
  const heroRef = useRef<HTMLDivElement>(null);

  const filtered = PRODUCTS.filter((p) => {
    const matchCat = activeCategory === 'All' || p.category === activeCategory;
    const matchSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.shortDescription.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  const featured = PRODUCTS.filter((p) => p.featured);

  useEffect(() => {
    const shuffle = () => {
      const arr = [...featured];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      setShuffledFeatured(arr);
    };

    shuffle(); // initial shuffle on mount
    const interval = setInterval(shuffle, 3000);
    return () => clearInterval(interval);
  }, []);

  const addToCart = (product: Product) => {
    if (product.stock === 0) return;
    setAddingId(product.id);
    try {
      const cart = JSON.parse(localStorage.getItem('cakepalace_cart') || '[]');
      const existing = cart.find((i: {productId: string;}) => i.productId === product.id);
      if (existing) {
        existing.quantity = Math.min(existing.quantity + 1, product.stock);
      } else {
        cart.push({ productId: product.id, quantity: 1, product });
      }
      localStorage.setItem('cakepalace_cart', JSON.stringify(cart));
      window.dispatchEvent(new Event('cartUpdated'));
      setCartToast(product.name);
      setTimeout(() => setCartToast(null), 2500);
    } catch (e) {console.error(e);}
    setTimeout(() => setAddingId(null), 600);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      {/* Toast */}
      {cartToast &&
      <div className="fixed bottom-6 right-4 sm:right-6 z-50 animate-slide-in-right">
          <div className="flex items-center gap-3 bg-foreground text-white px-4 py-3 rounded-md shadow-lg text-sm font-medium max-w-xs">
            <Icon name="CheckCircleIcon" size={18} className="text-success flex-shrink-0" variant="solid" />
            <span className="line-clamp-1">"{cartToast}" added to cart</span>
          </div>
        </div>
      }

      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative overflow-hidden grain-overlay"
        style={{
          background: 'linear-gradient(135deg, #1A1A1A 0%, #2D1E0A 40%, #C8860A 100%)',
          minHeight: '360px'
        }}>

        {/* Background blobs */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-20 blur-3xl"
        style={{ background: 'radial-gradient(circle, #F5E6C8 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full opacity-10 blur-3xl"
        style={{ background: 'radial-gradient(circle, #E09B1A 0%, transparent 70%)' }} />

        <div className="relative z-10 max-w-container mx-auto px-4 sm:px-6 py-10 sm:py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Text */}
            <div className="animate-fade-in-up">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs font-medium text-white/80 tracking-wider uppercase mb-4 sm:mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                Ghana&apos;s Premier Baking Store
              </span>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-white font-light leading-tight mb-4 sm:mb-6">
                Everything for the<br />
                <span className="italic" style={{ color: '#F5E6C8' }}>perfect cake.</span>
              </h1>
              <p className="text-white/70 text-sm sm:text-base leading-relaxed mb-6 sm:mb-8 max-w-md">Professional baking tools, premium ingredients, and catering supplies, delivered across Ghana.</p>
              <div className="flex flex-wrap gap-3">
                <a href="#catalog" className="btn-primary px-5 py-2.5 sm:px-6 sm:py-3 text-sm inline-flex items-center gap-2">
                  <Icon name="ShoppingBagIcon" size={18} />
                  Browse Products
                </a>
                <Link href="/cart" className="inline-flex items-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 text-sm font-semibold border border-white/30 text-white rounded-sm hover:bg-white/10 transition-colors">
                  <Icon name="ShoppingCartIcon" size={18} />
                  View Cart
                </Link>
              </div>
            </div>

            {/* Right: Floating product cards */}
            <div className="hidden lg:flex items-center justify-center relative h-80">
              {shuffledFeatured.slice(0, 3).map((product, i) => {
                const transforms = [
                'rotate(-4deg) translate(-30px, -10px)',
                'rotate(2deg) translate(20px, 10px)',
                'rotate(-1deg) translate(-10px, 30px)'];

                const delays = ['0s', '1.5s', '3s'];
                return (
                  <div
                    key={product.id}
                    className="absolute animate-float bg-white rounded-md shadow-lg overflow-hidden w-44"
                    style={{
                      transform: transforms[i],
                      animationDelay: delays[i],
                      zIndex: 3 - i
                    }}>

                    <div className="h-28 overflow-hidden">
                      <AppImage
                        src={product.image}
                        alt={product.name}
                        width={176}
                        height={112}
                        className="w-full h-full object-cover" />

                    </div>
                    <div className="p-2.5">
                      <p className="text-xs font-semibold text-foreground line-clamp-1">{product.name}</p>
                      <p className="text-xs font-bold text-primary mt-0.5">{formatPrice(product.price)}</p>
                    </div>
                  </div>);

              })}
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 40" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <path d="M0,40 C360,0 1080,0 1440,40 L1440,40 L0,40 Z" fill="#FDFAF5" />
          </svg>
        </div>
      </section>

      {/* Trust bar */}
      <div className="bg-white border-b border-border">
        <div className="max-w-container mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row flex-wrap justify-center sm:justify-between gap-3 py-4">
            {[
            { icon: 'TruckIcon', text: 'Free delivery in Accra on orders over ₵200' },
            { icon: 'ShieldCheckIcon', text: '100% authentic products' },
            { icon: 'ChatBubbleLeftEllipsisIcon', text: 'WhatsApp support: 024557827897' }].
            map((item, i) =>
            <div key={i} className="flex items-center gap-2 text-sm text-muted">
                <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-primary flex-shrink-0" />
                <span className="text-xs sm:text-sm">{item.text}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Catalog Section */}
      <main id="catalog" className="flex-1 max-w-container mx-auto px-4 sm:px-6 py-8 sm:py-14 w-full">
        {/* Search + Filter */}
        <div className="flex flex-col gap-4 mb-8">
          <div className="relative w-full max-w-md">
            <Icon name="MagnifyingGlassIcon" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-light" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-input pl-10" />
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) =>
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-sm text-xs sm:text-sm font-medium transition-all duration-200 ${
              activeCategory === cat ?
              'bg-primary text-white shadow-gold' :
              'bg-white border border-border text-muted hover:border-primary hover:text-primary'}`
              }>

                {cat}
              </button>
            )}
          </div>
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-muted">
            {filtered.length} {filtered.length === 1 ? 'product' : 'products'}
            {activeCategory !== 'All' && ` in ${activeCategory}`}
          </p>
        </div>

        {/* Product Grid */}
        {filtered.length === 0 ?
        <div className="text-center py-20">
            <Icon name="ArchiveBoxXMarkIcon" size={48} className="text-muted-light mx-auto mb-4" />
            <h3 className="font-display text-xl text-foreground mb-2">No products found</h3>
            <p className="text-muted text-sm">Try adjusting your search or filter</p>
            <button onClick={() => {setSearchQuery('');setActiveCategory('All');}}
          className="btn-outline px-5 py-2 text-sm mt-4">Clear Filters</button>
          </div> :

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {filtered.map((product, idx) => {
            const stockInfo = getStockLabel(product.stock);
            return (
              <div
                key={product.id}
                className="product-card bg-white rounded-md overflow-hidden border border-border"
                style={{ animationDelay: `${idx * 50}ms` }}>

                  {/* Image */}
                  <Link href={`/product-detail?id=${product.id}`} className="block relative overflow-hidden aspect-square">
                    <AppImage
                    src={product.image}
                    alt={product.name}
                    fill
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                    className="object-cover transition-transform duration-500 hover:scale-105" />

                    {product.featured &&
                  <span className="absolute top-2 left-2 bg-primary text-white text-xs font-semibold px-2 py-0.5 rounded-sm">
                        Featured
                      </span>
                  }
                    {product.stock === 0 &&
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="bg-white text-danger text-xs font-bold px-3 py-1 rounded-sm">Out of Stock</span>
                      </div>
                  }
                  </Link>

                  {/* Info */}
                  <div className="p-3 sm:p-4">
                    <span className="text-xs text-muted mb-1 block">{product.category}</span>
                    <Link href={`/product-detail?id=${product.id}`}>
                      <h3 className="font-semibold text-sm sm:text-base text-foreground hover:text-primary transition-colors leading-snug mb-1 line-clamp-2">
                        {product.name}
                      </h3>
                    </Link>
                    <p className="text-xs text-muted line-clamp-2 mb-3 hidden sm:block">{product.shortDescription}</p>

                    <div className="flex items-center justify-between mb-3">
                      <span className="font-display font-semibold text-base sm:text-lg text-primary">{formatPrice(product.price)}</span>
                      <span className={`badge-stock ${stockInfo.className}`}>{stockInfo.label}</span>
                    </div>

                    <button
                    onClick={() => addToCart(product)}
                    disabled={product.stock === 0}
                    className={`w-full py-2 text-sm font-semibold rounded-sm transition-all duration-200 flex items-center justify-center gap-1.5 ${
                    product.stock === 0 ?
                    'bg-border text-muted-light cursor-not-allowed' :
                    addingId === product.id ?
                    'bg-success text-white' : 'btn-primary'}`
                    }>

                      {addingId === product.id ?
                    <><Icon name="CheckIcon" size={16} variant="solid" /> Added!</> :

                    <><Icon name="ShoppingCartIcon" size={16} /> Add to Cart</>
                    }
                    </button>
                  </div>
                </div>);

          })}
          </div>
        }
      </main>

      <Footer />
    </div>);

}